import { useApiClient } from "../../../hooks/useApiClient";
import useSWR, { type KeyedMutator } from "swr";
import { type AcquireBookStatusResponse } from "../types";
import {
  isScrapingOutOfService,
  getRemainingCountWithErrorCount,
  getStatusString,
  type AcquireBookStatusString,
} from "../../../utils/utilsBookStatus";
import { validatePictureRequestDetailContract } from "../utils/pictureRequestContract";
import { retryUnlessContractViolation } from "../utils/swrErrorRetry";

interface UseAcquireBookStatus {
  data: AcquireBookStatusResponse | undefined;
  /** 取得失敗（ネットワークエラー・応答契約違反）。stale な data がある間も保持される */
  error: Error | undefined;
  getStatusString: (
    data: AcquireBookStatusResponse | undefined
  ) => AcquireBookStatusString | "";
  getRemainingCountWithErrorCount: (
    data: AcquireBookStatusResponse | undefined
  ) => [number | null, number | null];
  isScrapingOutOfService: () => boolean;
  mutate: KeyedMutator<AcquireBookStatusResponse>;
}

/** サーバーページングのページ状態。null は現行の全件取得（通常依頼） */
export interface AcquireBookServerPage {
  page: number;
  pageSize: number;
}

export const useAcquireBookStatus = (
  id: string | undefined,
  serverPage: AcquireBookServerPage | null = null
): UseAcquireBookStatus => {
  const { apiClient } = useApiClient();

  const fetcher = async (url: string): Promise<AcquireBookStatusResponse> => {
    const { data } = await apiClient.get<AcquireBookStatusResponse>(url);
    validatePictureRequestDetailContract(data, url);
    return data;
  };

  // DataLens 一括依頼（数万件明細）のみ limit/offset 付きキーでページ取得する。
  // ページ状態がキーに含まれるため、ページ遷移＝キー変更で再取得され、
  // 30秒ポーリングも現在ページのキーで再検証される
  const buildEndpoint = (requestId: string): string =>
    serverPage === null
      ? `/picture-request/${requestId}`
      : `/picture-request/${requestId}?limit=${serverPage.pageSize}&offset=${
          serverPage.page * serverPage.pageSize
        }`;
  const { data, error, mutate } = useSWR<AcquireBookStatusResponse, Error>(
    id === undefined ? null : buildEndpoint(id),
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 1000 * 30,
      // 応答契約違反のような恒常的な失敗だけ再試行を止める（通知・リクエストが倍増するため）。
      // 一過性の失敗は既定どおり再試行する。全再試行を止めると SWR はエラーが残っている間
      // refreshInterval の再取得もスキップするため、一度のエラーでポーリングが復旧しなくなる
      onErrorRetry: retryUnlessContractViolation,
    }
  );

  return {
    data,
    error,
    getStatusString,
    getRemainingCountWithErrorCount,
    isScrapingOutOfService,
    mutate,
  };
};
